/**
 * @fileoverview Utility functions for Autodesk Forge Viewer.
 */

import localize, { LocKeys } from "../../../../../constants/localizations";

/**
 * Creates the HTML content for translation-in-progress message.
 * @param {string} progress - Progress percentage (e.g., "76%")
 * @returns {string} HTML string for loading indicator
 */
export const createTranslatingMessageHTML = (progress = "") => `
	<div class="forge-viewer__message" style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; width: 100%;">
		<span class="loading-icon"></span>
		<p style="margin-top: 16px; text-align: center;">${localize(LocKeys.TRANSLATING_MODEL)}${progress ? ` - ${progress}` : ""}.</p>
	</div>
`;

/**
 * Safely fits the 3D model to the viewer viewport.
 * Only applies to 3D models, not 2D sheets.
 * @param {Autodesk.Viewing.GuiViewer3D} viewer - Viewer instance
 * @param {string} context - Description of when this is called (for logging)
 */
export const fitModelToView = (viewer, context) => {
	try {
		if (
			viewer?.model &&
			!viewer.model.is2d() &&
			typeof viewer.fitToView === "function"
		) {
			viewer.fitToView();
		}
	} catch (err) {
		console.warn(`Error fitting model to view (${context}):`, err);
	}
};

/**
 * Updates the CustomMarkupExtension with current view information.
 * @param {Autodesk.Viewing.GuiViewer3D} viewer - Viewer instance
 * @param {string} viewType - View type
 * @param {number} viewIndex - Index of the view
 */
export const updateMarkupExtensionView = (viewer, viewType, viewIndex) => {
	const markupExtension = viewer?.getExtension("CustomMarkupExtension");
	if (markupExtension && typeof markupExtension.updateViewInfo === "function") {
		markupExtension.updateViewInfo(viewType, viewIndex);
	}
};

/**
 * Cleans up a Forge viewer instance.
 * @param {import("react").MutableRefObject} viewerRef - Ref containing viewer instance
 */
export const cleanupViewer = (viewerRef) => {
	if (viewerRef.current) {
		try {
			const viewer = viewerRef.current;
			if (typeof viewer.tearDown === "function") {
				viewer.tearDown();
			}
			if (typeof viewer.finish === "function") {
				viewer.finish();
			}
			// Only call uninitialize if the viewer still has a valid DOM context
			if (
				typeof viewer.uninitialize === "function" &&
				viewer.container &&
				viewer.container.parentNode
			) {
				viewer.uninitialize();
			}
			viewerRef.current = null;
		} catch (err) {
			console.warn("Error cleaning up Forge viewer:", err);
		}
	}
};
